import { io, Socket } from 'socket.io-client';
import { store } from '../store';
import { updateDisplayStatus } from '../store/slices/displaySlice';

const WS_URL = import.meta.env.VITE_WS_URL || 'http://localhost:3000';

let socket: Socket | null = null;
let notificationCallback: ((message: string, severity: 'success' | 'error' | 'warning' | 'info') => void) | null = null;

/**
 * Set notification callback for WebSocket events
 * This allows the socket service to trigger notifications
 */
export const setNotificationCallback = (
  callback: (message: string, severity: 'success' | 'error' | 'warning' | 'info') => void
) => {
  notificationCallback = callback;
};

/**
 * Initialize WebSocket connection
 * Connects to the backend WebSocket server and sets up event listeners
 * Requirements: 9.1, 9.6
 */
export const initializeSocket = () => {
  const token = store.getState().auth.token;
  const workspaceId = store.getState().auth.currentWorkspace?.id;

  if (!token || !workspaceId) {
    console.warn('Cannot initialize socket: missing token or workspace');
    return null;
  }

  // Disconnect existing socket if any
  if (socket) {
    socket.disconnect();
  }

  // Create new socket connection
  socket = io(WS_URL, {
    auth: {
      token,
    },
    query: {
      workspaceId,
    },
    transports: ['websocket', 'polling'],
    reconnection: true,
    reconnectionDelay: 1000,
    reconnectionDelayMax: 5000,
    reconnectionAttempts: 5,
  });

  // Connection events
  socket.on('connect', () => {
    console.log('WebSocket connected');
    // Only show success notification, not on every reconnect
  });

  socket.on('disconnect', () => {
    console.log('WebSocket disconnected');
    // Don't show notification on disconnect to avoid spam
  });

  socket.on('connect_error', (error) => {
    console.error('WebSocket connection error:', error);
    // Don't show notification on every connection error to avoid spam
    // Only log to console for debugging
  });

  socket.on('reconnect', (attemptNumber) => {
    console.log('WebSocket reconnected after', attemptNumber, 'attempts');
    // Don't spam notifications on reconnect
  });

  // Display status updates
  // Real-time status updates from backend
  // Requirements: 9.1
  socket.on('display:status', (data: {
    displayId: string;
    status: string;
    lastHeartbeat: string;
  }) => {
    console.log('Display status update:', data);
    store.dispatch(updateDisplayStatus({
      id: data.displayId,
      status: data.status,
      lastHeartbeat: data.lastHeartbeat,
    }));
  });

  // Offline display notifications
  // Show notification when a display goes offline
  // Requirements: 9.6
  socket.on('display:offline', (data: {
    displayId: string;
    displayName: string;
    lastHeartbeat: string;
  }) => {
    console.log('Display went offline:', data);
    if (notificationCallback) {
      const lastSeen = new Date(data.lastHeartbeat).toLocaleString();
      notificationCallback(
        `Display "${data.displayName}" went offline. Last seen: ${lastSeen}`,
        'warning'
      );
    }
    
    // Update display status in store
    store.dispatch(updateDisplayStatus({
      id: data.displayId,
      status: 'offline',
      lastHeartbeat: data.lastHeartbeat,
    }));
  });

  // Display online notifications
  socket.on('display:online', (data: {
    displayId: string;
    displayName: string;
    lastHeartbeat: string;
  }) => {
    console.log('Display came online:', data);
    if (notificationCallback) {
      notificationCallback(
        `Display "${data.displayName}" is now online`,
        'success'
      );
    }
    
    // Update display status in store
    store.dispatch(updateDisplayStatus({
      id: data.displayId,
      status: 'online',
      lastHeartbeat: data.lastHeartbeat,
    }));
  });

  // Display error notifications
  socket.on('display:error', (data: {
    displayId: string;
    displayName: string;
    errorMessage: string;
    lastHeartbeat: string;
  }) => {
    console.log('Display error:', data);
    if (notificationCallback) {
      notificationCallback(
        `Display "${data.displayName}" reported an error: ${data.errorMessage}`,
        'error'
      );
    }
    
    // Update display status in store
    store.dispatch(updateDisplayStatus({
      id: data.displayId,
      status: 'error',
      lastHeartbeat: data.lastHeartbeat,
    }));
  });

  // Emergency activation notifications
  socket.on('emergency:activated', (data: {
    scheduleId: string;
    scheduleName: string;
    targetDisplays: string[];
  }) => {
    console.log('Emergency activated:', data);
    if (notificationCallback) {
      notificationCallback(
        `Emergency content "${data.scheduleName}" activated on ${data.targetDisplays.length} display(s)`,
        'warning'
      );
    }
  });

  // Emergency deactivation notifications
  socket.on('emergency:deactivated', (data: {
    scheduleId: string;
    scheduleName: string;
  }) => {
    console.log('Emergency deactivated:', data);
    if (notificationCallback) {
      notificationCallback(
        `Emergency content "${data.scheduleName}" deactivated`,
        'info'
      );
    }
  });

  // Content sync completion notifications
  socket.on('content:synced', (data: {
    displayId: string;
    displayName: string;
    syncedContentIds: string[];
    timestamp: string;
  }) => {
    console.log('Content synced:', data);
    if (notificationCallback) {
      notificationCallback(
        `Display "${data.displayName}" synced ${data.syncedContentIds.length} content item(s)`,
        'info'
      );
    }
  });

  return socket;
};

/**
 * Disconnect WebSocket connection
 */
export const disconnectSocket = () => {
  if (socket) {
    socket.disconnect();
    socket = null;
  }
};

/**
 * Get current socket instance
 */
export const getSocket = () => socket;

/**
 * Check if socket is connected
 */
export const isSocketConnected = () => {
  return socket?.connected || false;
};
